import React, { useEffect, useRef, useState } from "react";
import useIsMobile from "@/lib/useIsMobile";

/**
 * Global floating WhatsApp help button.
 * - Fixed bottom-right, ~56px circular, WhatsApp brand green (#25D366).
 * - Opens WhatsApp with a pre-filled message to the owner's number.
 * - A small "Need help finding something?" label reveals on first scroll OR
 *   after 5s, then auto-hides. No modal / form / popup — just the button.
 */
const PHONE = "919316127693";
const MESSAGE = "Hi Kirit Mapbazar— I'm looking for...";
const WA_LINK = `https://wa.me/${PHONE}?text=${encodeURIComponent(MESSAGE)}`;

export default function WhatsAppFab() {
  const [showLabel, setShowLabel] = useState(false);
  const shownRef = useRef(false);
  const isMobile = useIsMobile();
  const size = isMobile ? 46 : 56;
  const icon = isMobile ? 24 : 30;

  useEffect(() => {
    let hideTimer;
    let fallback;
    const reveal = () => {
      if (shownRef.current) return;
      shownRef.current = true;
      setShowLabel(true);
      window.removeEventListener("scroll", reveal, true);
      clearTimeout(fallback);
      hideTimer = setTimeout(() => setShowLabel(false), 4500);
    };
    // Reveal on the first scroll (capture phase catches inner scroll containers too).
    window.addEventListener("scroll", reveal, true);
    // ...or after 5 seconds, whichever comes first.
    fallback = setTimeout(reveal, 5000);
    return () => {
      window.removeEventListener("scroll", reveal, true);
      clearTimeout(fallback);
      clearTimeout(hideTimer);
    };
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        left: 16,
        bottom: 148,
        zIndex: 999,
        display: "flex",
        flexDirection: "row-reverse",
        alignItems: "center",
        gap: 10,
        pointerEvents: "none",
      }}
    >
      {showLabel && (
        <div
          data-testid="whatsapp-help-label"
          className="wa-help-label"
          style={{
            pointerEvents: "none",
            background: "#0F3D2E",
            color: "white",
            padding: "8px 13px",
            borderRadius: 100,
            fontSize: 12.5,
            fontWeight: 600,
            whiteSpace: "nowrap",
            boxShadow: "0 8px 22px rgba(15,61,46,0.28)",
          }}
        >
          Need help finding something?
        </div>
      )}
      <a
        data-testid="whatsapp-fab"
        href={WA_LINK}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp"
        className="wa-fab"
        style={{
          pointerEvents: "auto",
          width: size,
          height: size,
          borderRadius: "50%",
          background: "#25D366",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 8px 22px rgba(37,211,102,0.45)",
          textDecoration: "none",
          flexShrink: 0,
        }}
      >
        <svg width={icon} height={icon} viewBox="0 0 24 24" fill="white" aria-hidden="true">
          <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.045zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
        </svg>
      </a>
      <style>{`
        .wa-fab { transition: transform 0.18s ease, box-shadow 0.18s ease; }
        .wa-fab:hover { transform: scale(1.08); box-shadow: 0 12px 28px rgba(37,211,102,0.55); }
        .wa-fab:active { transform: scale(0.95); }
        .wa-help-label { animation: waLabelIn 0.32s cubic-bezier(.2,.8,.2,1) both; }
        @keyframes waLabelIn {
          0%   { opacity: 0; transform: translateX(12px) scale(0.92); }
          100% { opacity: 1; transform: translateX(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
